// Sprints: settings, lifecycle, membership, reports, retrospectives, time logging and the running timer.
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { del, get, patch, post, put } from "./client";
import { projectOfKey } from "@/lib/taskLinks";
import { taskPath } from "./testing";
import type { CompleteSprintRequest } from "./types/CompleteSprintRequest";
import type { CreateRetroItemRequest } from "./types/CreateRetroItemRequest";
import type { CreateSprintRequest } from "./types/CreateSprintRequest";
import type { DayOff } from "./types/DayOff";
import type { DayOffRequest } from "./types/DayOffRequest";
import type { LogTimeRequest } from "./types/LogTimeRequest";
import type { MoveToSprintRequest } from "./types/MoveToSprintRequest";
import type { RetroItem } from "./types/RetroItem";
import type { RunningTimer } from "./types/RunningTimer";
import type { Sprint } from "./types/Sprint";
import type { SprintCapacityRequest } from "./types/SprintCapacityRequest";
import type { SprintReport } from "./types/SprintReport";
import type { SprintSettings } from "./types/SprintSettings";
import type { TaskSummary } from "./types/TaskSummary";
import type { TimeEntry } from "./types/TimeEntry";
import type { UpdateRetroItemRequest } from "./types/UpdateRetroItemRequest";
import type { UpdateSprintRequest } from "./types/UpdateSprintRequest";
import type { UpdateSprintSettingsRequest } from "./types/UpdateSprintSettingsRequest";
import type { UpdateTimeEntryRequest } from "./types/UpdateTimeEntryRequest";
import type { Velocity } from "./types/Velocity";

const enc = encodeURIComponent;
const sprintsPath = (key: string) => `/projects/${enc(key)}/sprints`;

/** Everything a sprint change can move: sprint lists, reports, task lists and tasks, the project (its unit). */
export function invalidateSprints(qc: QueryClient, projectKey: string) {
  void qc.invalidateQueries({ queryKey: ["sprints", projectKey] });
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  void qc.invalidateQueries({ queryKey: ["task", projectKey] });
}

// ───────────── Settings ─────────────

export function useSprintSettings(projectKey: string) {
  return useQuery({
    queryKey: ["sprints", projectKey, "settings"],
    queryFn: () => get<SprintSettings>(`/projects/${enc(projectKey)}/sprint-settings`),
  });
}

export function useUpdateSprintSettings(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSprintSettingsRequest) => patch<SprintSettings>(`/projects/${enc(projectKey)}/sprint-settings`, body),
    onSuccess: (s) => {
      qc.setQueryData(["sprints", projectKey, "settings"], s);
      invalidateSprints(qc, projectKey);
      // The project carries the unit and the time logging switch the task views read.
      void qc.invalidateQueries({ queryKey: ["project", projectKey] });
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// ───────────── Sprints ─────────────

/** Active, planned by date, then completed newest first. */
export function useSprints(projectKey: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["sprints", projectKey, "list"],
    queryFn: () => get<Sprint[]>(sprintsPath(projectKey!)),
    enabled: !!projectKey && enabled,
  });
}

export function useSprint(projectKey: string, n: number) {
  return useQuery({
    queryKey: ["sprints", projectKey, "one", n],
    queryFn: () => get<Sprint>(`${sprintsPath(projectKey)}/${n}`),
    enabled: Number.isFinite(n),
  });
}

function useSprintMutation<V, R>(projectKey: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => invalidateSprints(qc, projectKey) });
}

export const useCreateSprint = (projectKey: string) =>
  useSprintMutation(projectKey, (b: CreateSprintRequest) => post<Sprint>(sprintsPath(projectKey), b));
export const useUpdateSprint = (projectKey: string) =>
  useSprintMutation(projectKey, ({ n, ...b }: UpdateSprintRequest & { n: number }) => patch<Sprint>(`${sprintsPath(projectKey)}/${n}`, b));
export const useDeleteSprint = (projectKey: string) =>
  useSprintMutation(projectKey, (n: number) => del(`${sprintsPath(projectKey)}/${n}`));
export const useStartSprint = (projectKey: string) =>
  useSprintMutation(projectKey, ({ n, ...b }: UpdateSprintRequest & { n: number }) => post<Sprint>(`${sprintsPath(projectKey)}/${n}/start`, b));
export const useCompleteSprint = (projectKey: string) =>
  useSprintMutation(projectKey, ({ n, ...b }: CompleteSprintRequest & { n: number }) => post<Sprint>(`${sprintsPath(projectKey)}/${n}/complete`, b));
export const useSprintCapacity = (projectKey: string) =>
  useSprintMutation(projectKey, ({ n, ...b }: SprintCapacityRequest & { n: number }) => put<Sprint>(`${sprintsPath(projectKey)}/${n}/capacity`, b));

/** Moves top-level tasks into a sprint or the backlog. The caller handles the optimistic part and its rollback. */
export function useMoveToSprint(projectKey: string) {
  return useMutation({ mutationFn: (b: MoveToSprintRequest) => post<TaskSummary[]>(`${sprintsPath(projectKey)}/move`, b) });
}

// ───────────── Reports ─────────────

export function useSprintReport(projectKey: string, n: number) {
  return useQuery({
    queryKey: ["sprints", projectKey, "report", n],
    queryFn: () => get<SprintReport>(`${sprintsPath(projectKey)}/${n}/report`),
  });
}

export function useVelocity(projectKey: string) {
  return useQuery({
    queryKey: ["sprints", projectKey, "velocity"],
    queryFn: () => get<Velocity>(`${sprintsPath(projectKey)}/velocity`),
  });
}

// ───────────── Retrospective ─────────────

const retroKey = (projectKey: string, n: number) => ["sprints", projectKey, "retro", n] as const;

export function useRetro(projectKey: string, n: number) {
  return useQuery({ queryKey: retroKey(projectKey, n), queryFn: () => get<RetroItem[]>(`${sprintsPath(projectKey)}/${n}/retro`) });
}

export function useRetroMutation(projectKey: string, n: number) {
  const qc = useQueryClient();
  const base = `${sprintsPath(projectKey)}/${n}/retro`;
  return useMutation({
    mutationFn: (
      a: ({ op: "create" } & CreateRetroItemRequest) | ({ op: "update"; id: string } & UpdateRetroItemRequest) | { op: "delete"; id: string },
    ): Promise<RetroItem | void> => {
      switch (a.op) {
        case "create": {
          const { op: _op, ...b } = a;
          return post<RetroItem>(base, b);
        }
        case "update": {
          const { op: _op, id, ...b } = a;
          return patch<RetroItem>(`${base}/${id}`, b);
        }
        case "delete":
          return del(`${base}/${a.id}`);
      }
    },
    onSuccess: (item, a) =>
      qc.setQueryData<RetroItem[]>(retroKey(projectKey, n), (cur) => {
        if (!cur) return cur;
        if (a.op === "delete") return cur.filter((x) => x.id !== a.id);
        if (!item) return cur;
        return a.op === "create" ? [...cur, item] : cur.map((x) => (x.id === item.id ? item : x));
      }),
  });
}

// ───────────── Time ─────────────

const timeKey = (projectKey: string, task: string) => ["task", projectKey, task.toUpperCase(), "time"] as const;

export function useTimeEntries(projectKey: string, task: string, enabled = true) {
  return useQuery({
    queryKey: timeKey(projectKey, task),
    queryFn: () => get<TimeEntry[]>(`${taskPath(projectKey, task)}/time`),
    enabled,
  });
}

/**
 * Logged time moves the task's logged and remaining numbers, its lists and the sprint's burndown. Time on a subtask
 * rolls up to its root, so every open task of the project refreshes.
 */
function invalidateTime(qc: QueryClient, projectKey: string) {
  void qc.invalidateQueries({ queryKey: ["task", projectKey] });
  void qc.invalidateQueries({ queryKey: ["tasks"] });
  void qc.invalidateQueries({ queryKey: ["sprints", projectKey] });
}

export function useTimeMutation(projectKey: string, task: string) {
  const qc = useQueryClient();
  const base = `${taskPath(projectKey, task)}/time`;
  return useMutation({
    mutationFn: (
      a: ({ op: "log" } & LogTimeRequest) | ({ op: "update"; id: string } & UpdateTimeEntryRequest) | { op: "delete"; id: string },
    ): Promise<TimeEntry | void> => {
      switch (a.op) {
        case "log": {
          const { op: _op, ...b } = a;
          return post<TimeEntry>(base, b);
        }
        case "update": {
          const { op: _op, id, ...b } = a;
          return patch<TimeEntry>(`${base}/${id}`, b);
        }
        case "delete":
          return del(`${base}/${a.id}`);
      }
    },
    onSuccess: () => invalidateTime(qc, projectKey),
  });
}

// ───────────── Timer ─────────────

const TIMER_KEY = ["me", "timer"] as const;

/** The caller's running timer, from the server: it survives reloads and shows in every tab. */
export function useTimer() {
  return useQuery({ queryKey: TIMER_KEY, queryFn: () => get<RunningTimer | null>("/me/timer"), staleTime: 60_000, refetchOnWindowFocus: true });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskKey: string) => post<RunningTimer>("/me/timer", { task_key: taskKey }),
    onSuccess: (timer) => {
      const before = qc.getQueryData<RunningTimer | null>(TIMER_KEY);
      qc.setQueryData(TIMER_KEY, timer);
      // Starting stops (and saves) a timer that was running on another task.
      if (before) invalidateTime(qc, before.task.project_key);
    },
  });
}

/** Stops the timer; the server saves its entry and returns it for adjusting. */
export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del<TimeEntry>("/me/timer"),
    onSuccess: (entry) => {
      const before = qc.getQueryData<RunningTimer | null>(TIMER_KEY);
      qc.setQueryData(TIMER_KEY, null);
      if (before) invalidateTime(qc, before.task.project_key);
      else if (entry) invalidateTime(qc, projectOfKey(entry.task_key));
    },
    // A timer stopped elsewhere (another tab, the 12-hour auto-stop): show the truth.
    onError: () => void qc.invalidateQueries({ queryKey: TIMER_KEY }),
  });
}

// ── Days off

export function useDaysOff(userId: string | null) {
  return useQuery({
    queryKey: ["days-off", userId ?? "me"],
    queryFn: () => get<DayOff[]>(userId ? `/days-off?user_id=${encodeURIComponent(userId)}` : "/days-off"),
  });
}

export function useDaysOffMutations(userId: string | null) {
  const qc = useQueryClient();
  const done = (list: DayOff[] | void) => {
    if (list) qc.setQueryData(["days-off", userId ?? "me"], list);
    void qc.invalidateQueries({ queryKey: ["days-off"] });
    // Planned sprints' capacity changes with them.
    void qc.invalidateQueries({ queryKey: ["sprints"] });
  };
  return {
    add: useMutation({ mutationFn: (body: DayOffRequest) => post<DayOff[]>("/days-off", { ...body, user_id: userId ?? undefined }), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, ...body }: DayOffRequest & { id: string }) => patch<DayOff[]>(`/days-off/${id}`, body), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: string) => del(`/days-off/${id}`), onSuccess: () => done() }),
  };
}
